/**
 * A dispensing job's notes.
 *
 * <lens-job-notes job="job"> is a small button showing how many notes the job has. Hovering over it
 * shows the notes, newest first; clicking it pins them open with a box to add one above them. Notes
 * are a log - they are appended, never edited or removed - and are saved on the job through the API.
 *
 * The popover is moved to <body> and placed against the button, because the button sits in
 * clickable job rows inside scrolling, overflow-clipped containers (and inside an md-dialog, whose
 * transform would otherwise anchor a fixed element to the dialog rather than the window).
 */
app.component("lensJobNotes", {
	template: `
<button type="button" class="lens-notes-btn" ng-class="{'has-notes': $ctrl.count(), 'open': $ctrl.show}"
		ng-mouseenter="$ctrl.hover(true)" ng-mouseleave="$ctrl.hover(false)" ng-click="$ctrl.pin($event)"
		aria-label="Job notes" data-testid="lens-job-notes-btn">
	<i class="fa" ng-class="$ctrl.count() ? 'fa-sticky-note' : 'fa-sticky-note-o'"></i>
	<span>{{$ctrl.count() ? $ctrl.count() : 'Note'}}</span>
</button>
<div class="lens-notes-pop" ng-show="$ctrl.show" ng-class="{'above': $ctrl.above, 'pinned': $ctrl.pinned}" ng-style="$ctrl.style"
		ng-mouseenter="$ctrl.hover(true)" ng-mouseleave="$ctrl.hover(false)" ng-click="$event.stopPropagation()">
	<div class="lens-notes-head">
		<span><i class="fa fa-sticky-note-o"></i> Job notes</span>
		<a class="pointer lens-notes-close" ng-if="$ctrl.pinned" ng-click="$ctrl.close()" aria-label="Close"><i class="fa fa-times"></i></a>
	</div>
	<div class="lens-notes-empty" ng-if="$ctrl.pinned && !$ctrl.job.id">Save the job to add notes to it.</div>
	<div class="lens-notes-add" ng-if="$ctrl.pinned && $ctrl.job.id">
		<textarea class="form-control" rows="2" ng-model="$ctrl.draft" ng-keydown="$ctrl.keydown($event)"
				placeholder="Add a note - Enter to save, Shift+Enter for a new line"></textarea>
		<div class="lens-notes-actions">
			<button type="button" class="btn btn-cancel font-medium" ng-click="$ctrl.close()">Close</button>
			<button type="button" class="btn btn-navy font-medium" ng-disabled="!$ctrl.draft.trim() || $ctrl.saving" ng-click="$ctrl.add()">{{$ctrl.saving ? 'Saving…' : 'Add note'}}</button>
		</div>
	</div>
	<div class="lens-notes-list">
		<div class="lens-notes-empty" ng-if="!$ctrl.count()">No notes yet{{$ctrl.pinned ? '' : ' - click to add one'}}.</div>
		<div class="lens-note" ng-repeat="n in $ctrl.job.notes | orderBy:'at':true track by $index">
			<div class="lens-note-meta"><b translate="no">{{n.by}}</b> &middot; {{n.at | date:'d MMM yyyy, h:mm a'}}</div>
			<div class="lens-note-text">{{n.text}}</div>
		</div>
	</div>
</div>`,
	bindings: {
		job: "<",
	},
	controller: "lensJobNotesController",
});

app.controller("lensJobNotesController", [
	"$scope",
	"$element",
	"$timeout",
	"$document",
	"$window",
	"LensDispensingStore",
	"toast",
	function ($scope, $element, $timeout, $document, $window, Store, toast) {
		const $ctrl = this;
		const WIDTH = 320;
		let pop = null;
		let button = null;
		let leaveTimer = null;

		$ctrl.show = false;
		$ctrl.pinned = false;
		$ctrl.draft = "";
		$ctrl.style = {};

		$ctrl.count = () => ($ctrl.job && $ctrl.job.notes ? $ctrl.job.notes.length : 0);

		$ctrl.$postLink = function () {
			button = $element[0].querySelector(".lens-notes-btn");
			pop = $element[0].querySelector(".lens-notes-pop");
			$document[0].body.appendChild(pop);
		};

		// Below the button, right edges aligned; above it when there isn't room underneath.
		function place() {
			if (!button) return;
			const r = button.getBoundingClientRect();
			const left = Math.max(8, Math.min(r.right - WIDTH, $window.innerWidth - WIDTH - 8));
			$ctrl.above = $window.innerHeight - r.bottom < 300 && r.top > $window.innerHeight - r.bottom;
			$ctrl.style = $ctrl.above
				? { left: left + "px", bottom: $window.innerHeight - r.top + 6 + "px", width: WIDTH + "px" }
				: { left: left + "px", top: r.bottom + 6 + "px", width: WIDTH + "px" };
		}

		function open() {
			place();
			$ctrl.show = true;
		}

		// Hovering previews the notes. A short grace period lets the pointer cross the gap from the
		// button onto the popover. Once pinned, only Close, a click outside or Escape shuts it.
		$ctrl.hover = function (over) {
			$timeout.cancel(leaveTimer);
			if ($ctrl.pinned) return;
			if (over) open();
			else leaveTimer = $timeout(() => ($ctrl.show = false), 180);
		};

		$ctrl.pin = function ($event) {
			// The button sits inside clickable job rows - don't open the job as well.
			if ($event) $event.stopPropagation();
			if ($ctrl.pinned) return $ctrl.close();
			$timeout.cancel(leaveTimer);
			$ctrl.pinned = true;
			open();
			$document.on("mousedown", outside);
			$document.on("keydown", escape);
			$timeout(() => {
				const box = pop.querySelector("textarea");
				if (box) box.focus();
				const list = pop.querySelector(".lens-notes-list");
				if (list) list.scrollTop = 0;
			});
		};

		$ctrl.close = function () {
			$ctrl.pinned = false;
			$ctrl.show = false;
			$ctrl.draft = "";
			$document.off("mousedown", outside);
			$document.off("keydown", escape);
		};

		$ctrl.add = function () {
			const text = ($ctrl.draft || "").trim();
			if (!text || $ctrl.saving || !$ctrl.job.id) return;
			$ctrl.saving = true;
			Store.addNote($ctrl.job.patientId, $ctrl.job, text)
				.then(function (saved) {
					$ctrl.job.notes = saved.notes;
					$ctrl.draft = "";
					$timeout(() => {
						const list = pop.querySelector(".lens-notes-list");
						if (list) list.scrollTop = 0;
						// Growing the list can push an "above" popover past the button - re-place it.
						place();
					});
				})
				.catch((resp) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, "The note could not be saved."), className: "alert-danger", dismissible: true }))
				.finally(() => ($ctrl.saving = false));
		};

		$ctrl.keydown = function ($event) {
			if ($event.key === "Enter" && !$event.shiftKey) {
				$event.preventDefault();
				$ctrl.add();
			}
		};

		function outside(e) {
			if (pop.contains(e.target) || button.contains(e.target)) return;
			$scope.$apply($ctrl.close);
		}

		function escape(e) {
			if (e.key !== "Escape") return;
			$scope.$apply($ctrl.close);
		}

		// Scrolling the job list moves the button out from under the popover: follow it while
		// pinned, and drop a hover preview.
		function onScroll(e) {
			// Scrolling the notes themselves is not the button moving.
			if (!$ctrl.show || (e && e.target && e.target.nodeType === 1 && pop.contains(e.target))) return;
			$scope.$applyAsync(() => ($ctrl.pinned ? place() : ($ctrl.show = false)));
		}
		$window.addEventListener("scroll", onScroll, true);
		$window.addEventListener("resize", onScroll);

		$ctrl.$onDestroy = function () {
			$timeout.cancel(leaveTimer);
			$document.off("mousedown", outside);
			$document.off("keydown", escape);
			$window.removeEventListener("scroll", onScroll, true);
			$window.removeEventListener("resize", onScroll);
			if (pop && pop.parentNode) pop.parentNode.removeChild(pop);
		};
	},
]);
